import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { LayoutRule } from '@ee/common/models';
import { PlaintiffOptions } from '@ee/common/utils';
import { IntakeLayoutRendererComponent } from './intake-layout-renderer.component';
import { addTenant, controlNamesOnPage, pageIsValid, withSectionTargets } from './intake-form-controls';
import { NO_RULES, RuleOutcome, evaluateRules } from './layout-rule-evaluation';
import { valuesForEvictionReasonRules } from './intake-submission';
import {
  FieldDefinitionView,
  LayoutPageView,
  LayoutSectionView,
  TENANT_FIELD_PREFIX,
  isTenantField,
  sameHeading
} from './intake-layout.model';
import { preflight, PreflightProblem, reviewSummary, ReviewGroup } from './intake-wizard-review';

/**
 * The form a client fills in, one authored page at a time.
 *
 * Everything on screen comes from the layout: the steps are `form.pages`, the cards are its sections,
 * the controls are its placements at their authored spans. The v1 wizard hard-codes four `mat-step`s
 * and names the section components inside each, so a firm could rearrange its form all day and the
 * wizard would not move — this is that defect fixed at the page level, the way the declared sections
 * fixed it at the field level.
 *
 * The stepper is hand-rolled rather than `mat-stepper`. The header has to show a per-page state that
 * `mat-step` does not model — answered, current, not yet reached — and a linear `mat-stepper` also
 * refuses backward navigation, which Review's "Change" links depend on.
 */
@Component({
  selector: 'ee-intake-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatIconModule, IntakeLayoutRendererComponent],
  template: `
    <div class="wizard">
      <!-- Where you are, and what is left. Every page the firm authored, in order. -->
      <ol class="wizard__steps">
        @if (leadingStepName) {
          <li class="wizard__step" [class.is-current]="step() === 0" [class.is-done]="step() > 0 && leadingStepValid">
            <span class="wizard__step-mark" aria-hidden="true">
              @if (step() > 0 && leadingStepValid) {
                <mat-icon fontSet="fa" fontIcon="fa-check" class="wizard-icon"></mat-icon>
              } @else {
                1
              }
            </span>
            <span class="wizard__step-name">{{ leadingStepName }}</span>
          </li>
        }
        @for (page of visiblePages(); track page.id; let index = $index) {
          <li class="wizard__step" [class.is-current]="stepOf(index) === step()" [class.is-done]="isDone(index)">
            <span class="wizard__step-mark" aria-hidden="true">
              @if (isDone(index)) {
                <mat-icon fontSet="fa" fontIcon="fa-check" class="wizard-icon"></mat-icon>
              } @else {
                {{ stepOf(index) + 1 }}
              }
            </span>
            <span class="wizard__step-name">{{ page.name }}</span>
          </li>
        }
      </ol>

      @if (onLeadingStep()) {
        <!--
          The firm's own step, in front of the client's.
          
          Which client, which workflow, which attorney, and the workflow's own intake fields — none of
          it is a question for the client, and all of it decides what the rest of the form and the case
          become. Projected rather than built here: it needs the eviction form service and the workflow
          catalog, neither of which a layout renderer should know about.
        -->
        <div class="wizard__body">
          <p class="wizard__counter">Step 1 of {{ totalSteps() }}</p>
          <h1 class="wizard__title">{{ leadingStepName }}</h1>
          @if (leadingStepNote) {
            <p class="wizard__lede">{{ leadingStepNote }}</p>
          }

          <div class="wizard__leading">
            <ng-content select="[wizardLeadingStep]"></ng-content>
          </div>

          <div class="wizard__actions">
            <span class="wizard__spacer"></span>
            <button type="button" class="wizard__continue" [disabled]="!leadingStepValid" (click)="next()">
              Continue
            </button>
          </div>
        </div>
      } @else if (current(); as page) {
        <div class="wizard__body">
          <p class="wizard__counter">Step {{ step() + 1 }} of {{ totalSteps() }}</p>
          <h1 class="wizard__title">{{ page.name }}</h1>
          @if (page.note) {
            <p class="wizard__lede">{{ page.note }}</p>
          }

          @if (isReview(page)) {
            <!--
              Generated, not authored. A summary written by hand goes stale the first time a firm adds a
              field, and a client would be asked to confirm a form that is no longer the one they filled.
            -->
            @if (problems().length) {
              <section class="wizard__preflight" role="alert">
                <h2 class="wizard__preflight-title">
                  {{ problems().length }}
                  {{ problems().length === 1 ? 'thing' : 'things' }} to fix before you can file
                </h2>
                <ul class="wizard__problems">
                  @for (problem of problems(); track problem.key) {
                    <li class="wizard__problem">
                      <mat-icon fontSet="fa" fontIcon="fa-circle-exclamation" class="wizard-icon is-problem"></mat-icon>
                      <span class="wizard__problem-text">
                        <span class="wizard__problem-what">{{ problem.title }}</span>
                        <!-- Why it is needed, not that it is needed. A client can act on the first. -->
                        <span class="wizard__problem-why">{{ problem.why }}</span>
                      </span>
                      <button type="button" class="wizard__fix" (click)="goTo(stepOf(problem.pageIndex))">
                        {{ problem.upload ? 'Upload' : 'Fix it' }}
                      </button>
                    </li>
                  }
                </ul>
              </section>
            } @else {
              <section class="wizard__preflight is-clear" role="status">
                <h2 class="wizard__preflight-title">Everything checks out</h2>
                <p class="wizard__preflight-count">{{ checkCount() }} checks passed</p>
              </section>
            }

            @for (group of summary(); track group.title) {
              <section class="wizard__summary">
                <header class="wizard__summary-header">
                  <h2 class="wizard__summary-title">{{ group.title }}</h2>
                  <button type="button" class="wizard__change" (click)="goTo(stepOf(group.pageIndex))">Change</button>
                </header>
                <dl class="wizard__summary-rows">
                  @for (row of group.rows; track row.label) {
                    <div class="wizard__summary-row">
                      <dt>{{ row.label }}</dt>
                      <dd>{{ row.value || '—' }}</dd>
                    </div>
                  }
                </dl>
              </section>
            }
          } @else {
            @for (section of sectionsOf(page); track section.key) {
              <section class="ee__intake-card wizard__card">
                <!-- A section named like its page (the Tenants page's one Tenant section) says nothing new. -->
                @if (!repeatsPage(page, section) || section.description) {
                  <header class="ee__intake-card-header">
                    @if (!repeatsPage(page, section)) {
                      <h2 class="ee__intake-card-title">{{ section.title }}</h2>
                    }
                    @if (section.description) {
                      <p class="ee__intake-card-lede">{{ section.description }}</p>
                    }
                  </header>
                }

                @if (isTenantSection(section)) {
                  <!--
                    One panel per tenant, because the count is the client's to decide. The rows are the
                    same authored rows every time — bound to that tenant's own group rather than the
                    root, which is the whole reason the renderer takes a form rather than reaching for
                    one.
                  -->
                  @for (tenant of tenantGroups(); track $index; let index = $index) {
                    <div class="wizard__party" [class.is-open]="openTenant() === index">
                      <!-- Remove is beside the toggle, not inside it: a button nested in a button is not focusable. -->
                      <div class="wizard__party-bar">
                        <button type="button" class="wizard__party-header" (click)="toggleTenant(index)">
                          <span class="wizard__party-number">{{ index + 1 }}</span>
                          <span class="wizard__party-name">{{ tenantName(tenant, index) }}</span>
                          <mat-icon
                            fontSet="fa"
                            [fontIcon]="openTenant() === index ? 'fa-chevron-up' : 'fa-chevron-down'"
                            class="wizard-icon"></mat-icon>
                        </button>
                        @if (tenantGroups().length > 1) {
                          <button type="button" class="wizard__party-remove"
                                  [attr.aria-label]="'Remove ' + tenantName(tenant, index)"
                                  (click)="removeTenant(index)">Remove</button>
                        }
                      </div>
                      @if (openTenant() === index) {
                        <div class="wizard__party-body">
                          <ee-intake-layout-renderer
                            [rows]="section.rows"
                            [definitions]="definitions"
                            [form]="tenant"
                            [unitTypes]="unitTypes"
                            [hidden]="rules().hidden"
                            [reasons]="rules().reasons"></ee-intake-layout-renderer>
                        </div>
                      }
                    </div>
                  }
                  <button type="button" class="wizard__add-party" (click)="onAddTenant()">
                    <mat-icon fontSet="fa" fontIcon="fa-plus" class="wizard-icon"></mat-icon>
                    <span>Add another tenant</span>
                  </button>
                } @else {
                  <ee-intake-layout-renderer
                    [rows]="section.rows"
                    [definitions]="definitions"
                    [form]="form"
                    [unitTypes]="unitTypes"
                    [hidden]="rules().hidden"
                    [disabled]="rules().disabled"
                    [reasons]="rules().reasons"
                    [showTotal]="showTotals"
                    [heading]="repeatsPage(page, section) ? page.name : section.title"
                    [plaintiffOptions]="plaintiffOptions"
                    [liveComposites]="true"></ee-intake-layout-renderer>
                }
              </section>
            }
          }

          <div class="wizard__actions">
            @if (step() > 0) {
              <button type="button" class="wizard__back" (click)="back()">Back</button>
            }
            <span class="wizard__spacer"></span>
            @if (isReview(page)) {
              @if (!problems().length) {
                <span class="wizard__ready">Ready to file</span>
              }
              <button type="button" class="wizard__submit" [disabled]="problems().length > 0 || submitting"
                      (click)="submit.emit()">
                {{ submitLabel }}
              </button>
            } @else {
              <!--
                Blocked on *this* page only. Validating the whole form would stop someone on page two
                over something unanswered on page four, with nothing on screen to explain it.
              -->
              <button type="button" class="wizard__continue" [disabled]="!canContinue()" (click)="next()">
                Continue
              </button>
            }
          </div>
        </div>
      }
    </div>
  `,
  styleUrl: './intake-wizard.component.scss'
})
export class IntakeWizardComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) pages: LayoutPageView[] = [];

  @Input({ required: true }) form!: FormGroup;

  @Input() definitions: Map<string, FieldDefinitionView> = new Map();

  @Input() rules_: LayoutRule[] = [];

  @Input() unitTypes: string[] = [];

  /** EE-367: the client account's plaintiff options for the `plaintiff` field. Null keeps a text field. */
  @Input() plaintiffOptions: PlaintiffOptions | null = null;

  /**
   * A firm-facing step in front of the authored pages, when the firm is the one filling the form in.
   *
   * Absent on the public link and for a client filling their own: they have no client to choose, no
   * workflow to pick and no intake fields to set. Named by the host, because only the host knows what
   * it is projecting.
   */
  @Input() leadingStepName?: string;

  @Input() leadingStepNote?: string;

  /** Whether that step is answered. The host owns its form, so it owns the answer. */
  @Input() leadingStepValid = true;

  /** The host's request is in flight: Submit stays disabled so a second press cannot file twice. */
  @Input() submitting = false;

  /** The submit button's words; the attorney client files an eviction, a PM creates a case. */
  @Input() submitLabel = 'Submit eviction';

  /** Draw each section's Total line (`include_in_total` / `subtract_from_total`). PM claims use it. */
  @Input() showTotals = false;

  @Output() submit = new EventEmitter<void>();

  /** Emitted whenever the client moves, so a host can persist a resumable draft. */
  @Output() stepChange = new EventEmitter<number>();

  protected readonly step = signal(0);
  protected readonly openTenant = signal(0);

  /** Re-evaluated on every value change; a client cannot wait for a round trip to learn what applies. */
  protected readonly rules = signal<RuleOutcome>(NO_RULES);

  private watching?: Subscription;

  /**
   * Re-evaluate now, and follow the form for as long as it is the same form.
   *
   * Subscribed on the *form* changing rather than on every input change: doing it unconditionally left
   * one live subscription per change, all of them recomputing on every keystroke. And the previous one
   * is torn down first, because a host that swaps the form — a resumed draft replacing an empty one —
   * would otherwise keep evaluating rules against a group nothing is bound to any more.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['form']) {
      this.watching?.unsubscribe();
      this.watching = this.form?.valueChanges.subscribe(() => this.recomputeRules());
    }
    this.recomputeRules();
  }

  /**
   * Also on init, so a host that binds imperatively still gets evaluated rules.
   *
   * `ngOnChanges` only fires for inputs Angular knows changed. A host that assigns the fields directly
   * — which a test does, and which a dynamically-created component does — would otherwise render a form
   * with every rule inert, and inert rules look exactly like no rules.
   */
  ngOnInit(): void {
    if (!this.watching) {
      this.watching = this.form?.valueChanges.subscribe(() => this.recomputeRules());
    }
    this.recomputeRules();
  }

  ngOnDestroy(): void {
    this.watching?.unsubscribe();
  }

  /**
   * The pages a client actually walks.
   *
   * A page whose every section the rules have hidden is not a step — it would be a numbered heading
   * with nothing under it and a Continue button that does nothing.
   */
  protected visiblePages(): LayoutPageView[] {
    return this.pages.filter((page) => this.isReview(page) || this.sectionsOf(page).length > 0);
  }

  /** How many steps in total, the firm's included. */
  protected totalSteps(): number {
    return this.visiblePages().length + (this.leadingStepName ? 1 : 0);
  }

  /** The step index an authored page sits at, shifted by the firm's step when there is one. */
  protected stepOf(pageIndex: number): number {
    return pageIndex + (this.leadingStepName ? 1 : 0);
  }

  protected onLeadingStep(): boolean {
    return !!this.leadingStepName && this.step() === 0;
  }

  protected current(): LayoutPageView | null {
    if (this.onLeadingStep()) {
      return null;
    }
    return this.visiblePages()[this.step() - (this.leadingStepName ? 1 : 0)] ?? null;
  }

  /** Review is generated rather than authored, so it holds no sections — that is how it is known. */
  protected isReview(page: LayoutPageView): boolean {
    return !page.sections.length;
  }

  /** Whether a section's title only repeats its page's ("Tenant" under "Tenants"). */
  protected repeatsPage(page: LayoutPageView, section: LayoutSectionView): boolean {
    return sameHeading(section.title, page.name);
  }

  protected sectionsOf(page: LayoutPageView): LayoutSectionView[] {
    return page.sections.filter((section) => !this.rules().hidden.has(section.key));
  }

  protected isTenantSection(section: LayoutSectionView): boolean {
    const items = section.rows.flatMap((row) => row.items).filter((item) => item.field_key);
    return items.length > 0 && items.every((item) => isTenantField(item.field_key));
  }

  protected tenantGroups(): FormGroup[] {
    return ((this.form.get('tenants') as FormArray | null)?.controls ?? []) as FormGroup[];
  }

  /** "Marcus Whitfield", or "Tenant 2" until they have a name to show. */
  protected tenantName(tenant: FormGroup, index: number): string {
    const first = (tenant.get('first_name')?.value ?? '').toString().trim();
    const last = (tenant.get('last_name')?.value ?? '').toString().trim();
    return [first, last].filter(Boolean).join(' ') || `Tenant ${index + 1}`;
  }

  protected toggleTenant(index: number): void {
    this.openTenant.set(this.openTenant() === index ? -1 : index);
  }

  protected onAddTenant(): void {
    addTenant(this.form, this.pages, this.definitions);
    this.openTenant.set(this.tenantGroups().length - 1);
  }

  protected removeTenant(index: number): void {
    const tenants = this.form.get('tenants') as FormArray | null;
    if (!tenants || tenants.length <= 1) {
      return;
    }
    tenants.removeAt(index);
    this.openTenant.set(Math.min(this.openTenant(), tenants.length - 1));
  }

  protected isDone(index: number): boolean {
    const page = this.visiblePages()[index];
    return this.stepOf(index) < this.step() && !!page && pageIsValid(this.form, page, this.rules());
  }

  protected canContinue(): boolean {
    const page = this.current();
    return !!page && pageIsValid(this.form, page, this.rules());
  }

  protected next(): void {
    this.goTo(Math.min(this.step() + 1, this.totalSteps() - 1));
  }

  protected back(): void {
    this.goTo(Math.max(this.step() - 1, 0));
  }

  protected goTo(index: number): void {
    this.step.set(index);
    this.stepChange.emit(index);
  }

  protected problems(): PreflightProblem[] {
    return preflight(this.visiblePages(), this.form, this.definitions, this.rules());
  }

  /**
   * How many checks passed, for the cleared state.
   *
   * Counted from what was actually checked rather than a constant: "5 checks passed" under a form with
   * forty questions on it is a number that means nothing.
   */
  protected checkCount(): number {
    return this.visiblePages()
      .filter((page) => !this.isReview(page))
      .reduce((total, page) => total + controlNamesOnPage(page).length, 0);
  }

  protected summary(): ReviewGroup[] {
    return reviewSummary(this.visiblePages(), this.form, this.definitions, this.rules());
  }

  /** Controls this wizard disabled because a rule said so — re-enabled when the rule stops matching. */
  private readonly disabledByRule = new Set<string>();

  private recomputeRules(): void {
    const outcome = withSectionTargets(
      evaluateRules(
        this.rules_ ?? [],
        valuesForEvictionReasonRules((this.form?.getRawValue() ?? {}) as Record<string, unknown>)
      ),
      this.pages ?? []
    );
    this.rules.set(outcome);
    // Disable, never hide: the value stays and is submitted (getRawValue), the client just cannot edit it.
    Object.keys(this.form?.controls ?? {}).forEach((key) => this.applyDisable(key, outcome.disabled.has(key)));

    // A rule on a tenant field targets `tenants.<field>`, which lives in every row of the tenants array,
    // so the top-level pass never reaches it. Tracked per row path; a removed row's path is let go.
    const tenants = this.form?.get('tenants');
    if (tenants instanceof FormArray) {
      tenants.controls.forEach((row, index) =>
        Object.keys((row as FormGroup).controls ?? {}).forEach((name) =>
          this.applyDisable(`tenants.${index}.${name}`, outcome.disabled.has(`${TENANT_FIELD_PREFIX}${name}`))
        )
      );
      [...this.disabledByRule]
        .filter((path) => path.startsWith('tenants.') && !this.form.get(path))
        .forEach((path) => this.disabledByRule.delete(path));
    }
  }

  private applyDisable(path: string, disable: boolean): void {
    const control = this.form.get(path);
    if (!control) {
      return;
    }
    if (disable && control.enabled) {
      control.disable({ emitEvent: false });
      this.disabledByRule.add(path);
    } else if (!disable && this.disabledByRule.has(path)) {
      control.enable({ emitEvent: false });
      this.disabledByRule.delete(path);
    }
  }
}
